import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import AccordionListPanel, { AccordionItemData } from "../../../generic/accordion/AccordionListPanel";
import { RulesPanelProps, useRulesPanel } from "./RulesPanelHook";
import { formatMessage, formatMessageWithParams } from "../../../../utils/helpers/LanguageHelper";
import { Rule, RuleType } from "../../../interfaces/Rule";
import { OperationCompletedPanel } from "../../../layout/OperationCompletedPanel";
import { CustomModal } from "../../../layout/ActionModal";
import { RulesResultPanel, RunResult } from "../../../rules/RulesResultPanel";
import { sortResults } from "../../../rules/runStatus";
import { MULTI_BATCH, RESULTS_ORDERING_MODE, RESULTS_REORDER_COALESCING } from "../../../rules/runConfig";
import FooterPanel from "../../../layout/FooterPanel";
import { AddRuleModal } from "./AddRuleModal";
import {
  getRuleBodyAsText,
  renderConsistencyContent,
  renderScriptWithTagPills,
  renderTableTotalsContent,
} from "../../../../utils/helpers/RulesHelper";
import { Modal } from "@fluentui/react";
import { selectCellOrContentControl } from "../../../../office/ContentControl";
import { runRulesStreaming } from "../../../../utils/helpers/RunController";
import type { RunController } from "../../../../utils/helpers/RunController";
import { generateIdentifier } from "../../../../utils/helpers/RobotFramework";

/**
 * Reorders a full row snapshot to match `order` (the rule-id sequence the run was selected in),
 * instead of trusting the snapshot's own array order. Keeps "fixed-then-sort" mode's rows in
 * selection order for the whole run — a streamed update can change a row's content in place but
 * never move it. A `ruleId` in `order` with no matching row is skipped.
 */
function anchorToSelectionOrder(rows: RunResult[], order: string[]): RunResult[] {
  const byRuleId = new Map(rows.map((row) => [row.ruleId, row] as const));
  return order.map((ruleId) => byRuleId.get(ruleId)).filter((row): row is RunResult => Boolean(row));
}

/**
 * Applies the shared `sortResults` comparator to a full row snapshot. Stable sort, so tied rows
 * (equal status+title) keep their existing relative order across repeated calls. The one
 * comparator-based-sort call site in this component — both `onRowsChanged`'s live branch and
 * `onCompleted` funnel through it.
 */
function applySortedOrder(rows: RunResult[]): RunResult[] {
  return [...rows].sort(sortResults);
}

/**
 * Fires when one of `runSelected`'s two coalescing timers elapses. Cancels whichever other timer
 * is still armed (whichever fires first performs the sort and cancels the other), then applies the
 * sort via `applySortedOrder` if this run is still current.
 */
function fireCoalescedResort(
  firedTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  otherTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  isCurrentRun: () => boolean,
  setResults: React.Dispatch<React.SetStateAction<RunResult[]>>
): void {
  firedTimerRef.current = null;
  if (otherTimerRef.current !== null) {
    clearTimeout(otherTimerRef.current);
    otherTimerRef.current = null;
  }
  if (!isCurrentRun()) return;
  setResults((current) => applySortedOrder(current));
}

/**
 * Merges a full row snapshot into `previous`'s existing order — used once a run is finalized so a
 * late row update (e.g. the background format pass calling `setRow` after `onCompleted` already
 * fired) can only change a row's own content in place, never move it or re-trigger a sort. A
 * `ruleId` present in `incoming` but missing from `previous` is appended rather than dropped.
 */
function mergeRowsPreservingOrder(previous: RunResult[], incoming: RunResult[]): RunResult[] {
  const incomingByRuleId = new Map(incoming.map((row) => [row.ruleId, row] as const));
  const merged = previous.map((row) => incomingByRuleId.get(row.ruleId) ?? row);
  const knownRuleIds = new Set(previous.map((row) => row.ruleId));
  const extra = incoming.filter((row) => !knownRuleIds.has(row.ruleId));
  return [...merged, ...extra];
}

/**
 * Synchronously predicts whether the rule at `index` (selection order) starts life RUNNING or
 * QUEUED, replicating the pure rule-count chunk-boundary math (`maxRulesPerBatch`-sized chunks; the
 * first `maxConcurrentBatches` chunks start immediately) — not the real packer, which also closes a
 * chunk early on script/context-key caps this synchronous function cannot know.
 *
 * This is a conservative approximation: script/context caps can only make a chunk smaller than the
 * rule-count boundary, never larger, so this can only ever over-predict RUNNING (never under-predict
 * QUEUED for a rule that actually starts immediately). Any over-prediction self-corrects within one
 * render, once the controller's own synchronous seed arrives.
 *
 * When `MULTI_BATCH.enabled` is `false`, every index maps to `"RUNNING"`, matching the original
 * pre-multi-batch behavior.
 */
function predictInitialStatus(index: number, totalRules: number): "RUNNING" | "QUEUED" {
  if (!MULTI_BATCH.enabled) return "RUNNING";
  const chunkSize = MULTI_BATCH.maxRulesPerBatch > 0 ? MULTI_BATCH.maxRulesPerBatch : totalRules || 1;
  const chunkIndex = Math.floor(index / chunkSize);
  return chunkIndex < MULTI_BATCH.maxConcurrentBatches ? "RUNNING" : "QUEUED";
}

/* -----------------------------------------
   Component
----------------------------------------- */
export const RulesPanel: FunctionComponent<RulesPanelProps> = (props) => {
  const [results, setResults] = useState<RunResult[]>([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // The streaming controller backing whatever is currently shown in the results panel (if any),
  // plus a monotonically increasing token. Every handler passed to `runRulesStreaming` closes over
  // the token current when it started and re-checks it before touching state, so a superseded run's
  // late callbacks can never clobber a newer run's rows.
  const controllerRef = useRef<RunController | null>(null);
  const runTokenRef = useRef(0);

  // The rule-id sequence the current run was selected/seeded in. `onRowsChanged`/`onCompleted`
  // re-anchor every incoming snapshot to this order rather than trusting the snapshot's own array
  // order — the "fixed-then-sort" contract.
  const selectionOrderRef = useRef<string[]>([]);

  // Flips to `true` once `onCompleted` applies the run's single completion sort. While `true`, a
  // later `onRowsChanged` snapshot is merged into the current displayed order instead of being
  // re-anchored to selection order, which would otherwise undo the completion sort.
  const finalizedRef = useRef(false);

  // Refs backing the live-mode deferred re-sort behind `RESULTS_REORDER_COALESCING`. `quietTimerRef`
  // fires `quietWindowMs` after the most recent update, reset on every new one. `maxHoldTimerRef` is
  // armed once per deferral window and never reset, so a steady trickle of events can't defer the
  // sort forever. Whichever timer fires first performs the sort and cancels the other.
  const quietTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancels both coalescing timers, if armed. Called before a run's final sort, when a new run
  // starts, and on unmount, so a stray timer can never fire and touch state afterward.
  const clearCoalescingTimers = () => {
    if (quietTimerRef.current !== null) {
      clearTimeout(quietTimerRef.current);
      quietTimerRef.current = null;
    }
    if (maxHoldTimerRef.current !== null) {
      clearTimeout(maxHoldTimerRef.current);
      maxHoldTimerRef.current = null;
    }
  };

  const {
    rules = [],
    filteredRules = [],
    expanded,
    showAddRulesModal,
    setShowAddRulesModal,
    search,
    handleAccordionToggle,
    handleFilter,
    actionGroups,
    ruleToEdit,
    setRuleToEdit,
    deleteConfirm,
    ruleToDelete,
    confirmDelete,
    cancelDelete,
  } = useRulesPanel({
    ...props,
    // Shares the same immediate-streaming entry point as the multi-select Run/Run all footer button.
    onRunRule: (ruleId) => {
      void runSelected([ruleId]);
    },
  });
  const mapRuleToAccordionItem = (rule: Rule): AccordionItemData<Rule> => ({
    id: rule.id,
    title: rule.title,
    data: rule,
  });

  const accordionItems = useMemo(() => rules.map(mapRuleToAccordionItem), [rules]);
  const filteredAccordionItems = useMemo(() => filteredRules.map(mapRuleToAccordionItem), [filteredRules]);

  const renderRuleBody = (rule: Rule): React.ReactNode => {
    if (rule.type === RuleType.CONSISTENCY) return renderConsistencyContent(rule, props.tags ?? []);
    if (rule.type === RuleType.TABLE_TOTALS) return renderTableTotalsContent(rule);
    return renderScriptWithTagPills(rule.script, props.tags ?? []);
  };

  const renderItemContent = (item: AccordionItemData<Rule>, isExpanded: boolean) => {
    if (!isExpanded) return null;
    const rule = item.data;
    return (
      <div style={{ paddingBottom: "12px" }}>
        <div
          style={{ fontSize: "14px", lineHeight: "18px" }}
          id={generateIdentifier(rule.title, "rule_body")}
          aria-label={getRuleBodyAsText(rule, props.tags ?? [])}
        >
          {renderRuleBody(rule)}
        </div>
      </div>
    );
  };

  const hasRules = rules.length > 0;

  /**
   * Minimal placeholder row shown the instant a rule is selected to run, before any async work, so
   * the panel is never blank even for a fraction of a second. Shape matches what `RunController`'s
   * own internal seeding builds for a non-early row. `status` comes from `predictInitialStatus` so
   * a not-yet-started chunk is seeded QUEUED instead of a guess the controller would contradict.
   */
  const buildPlaceholderRow = (rule: Rule, now: string, status: "RUNNING" | "QUEUED"): RunResult => ({
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: rule.type,
    info: { status, timestamp: now },
  });

  const runSelected = async (ids: string[]) => {
    if (!ids || ids.length === 0) return;

    // Cancel whatever run is in flight before starting a new one. `isRunning` is just a "nothing to
    // cancel" optimization — cancelRun() is idempotent and a safe no-op regardless.
    if (isRunning) {
      void controllerRef.current?.cancelRun();
    }

    // Bump the token before seeding anything below — every handler passed to `runRulesStreaming`
    // re-checks it before touching state, so it becomes a no-op once a newer run starts.
    const token = ++runTokenRef.current;
    const isCurrentRun = () => runTokenRef.current === token;

    const selectedRules = ids.map((id) => rules.find((r) => r.id === id)).filter((r): r is Rule => Boolean(r));
    if (selectedRules.length === 0) return;

    const now = new Date().toISOString();
    // Lock in the selection order before any streamed snapshot can arrive — captured in both
    // modes, never from the sorted seed below, or fixed-then-sort would lose its own contract.
    selectionOrderRef.current = selectedRules.map((rule) => rule.id);
    // A freshly started run is never finalized yet.
    finalizedRef.current = false;
    // Cancel any coalescing timers left over from the preceding run.
    clearCoalescingTimers();
    const initialRows = selectedRules.map((rule, index) =>
      buildPlaceholderRow(rule, now, predictInitialStatus(index, selectedRules.length))
    );
    // Live mode seeds placeholders already in comparator order, so the panel never visibly
    // reorders on the first snapshot. fixed-then-sort keeps seeding in selection order.
    setResults(RESULTS_ORDERING_MODE === "live" ? applySortedOrder(initialRows) : initialRows);
    setResultsOpen(true);
    setIsRunning(true);

    // Arms/resets the two coalescing timers for the current run. Only called from the `live`
    // branch of `onRowsChanged`, and only when `RESULTS_REORDER_COALESCING.enabled`.
    const scheduleDeferredResort = () => {
      // Quiet-window timer: fires `quietWindowMs` after the most recent update — reset every call.
      if (quietTimerRef.current !== null) {
        clearTimeout(quietTimerRef.current);
      }
      quietTimerRef.current = setTimeout(
        () => fireCoalescedResort(quietTimerRef, maxHoldTimerRef, isCurrentRun, setResults),
        RESULTS_REORDER_COALESCING.quietWindowMs
      );

      // Max-hold ceiling: armed once per deferral window, never reset, so a steady trickle of
      // events can't defer the sort forever.
      if (maxHoldTimerRef.current === null) {
        maxHoldTimerRef.current = setTimeout(
          () => fireCoalescedResort(maxHoldTimerRef, quietTimerRef, isCurrentRun, setResults),
          RESULTS_REORDER_COALESCING.maxHoldMs
        );
      }
    };

    let controller: RunController;
    try {
      controller = await runRulesStreaming(ids, rules, props.tags ?? [], {
        // `onRowsChanged` always carries the full snapshot, so a plain `setResults` updates
        // everything in place — `onRowUpdate` is left unused to avoid a redundant state update.
        onRowsChanged: (rows) => {
          if (!isCurrentRun()) return;
          if (RESULTS_ORDERING_MODE === "fixed-then-sort") {
            if (finalizedRef.current) {
              // The completion sort was already applied (e.g. a late format-pass update after
              // onCompleted) — merge into the current order instead of re-anchoring, which would
              // undo the completion sort.
              setResults((previous) => mergeRowsPreservingOrder(previous, rows));
            } else {
              // Rows stay in selection order for the whole run — anchor the incoming snapshot
              // rather than trusting its own array order; content updates in place, never reorders.
              setResults(anchorToSelectionOrder(rows, selectionOrderRef.current));
            }
          } else if (RESULTS_REORDER_COALESCING.enabled) {
            // Content updates in place immediately, but the re-sort is deferred until the stream
            // goes quiet (or maxHoldMs forces it), so a fast run doesn't interrupt its own FLIP
            // animation on every event.
            setResults((previous) => mergeRowsPreservingOrder(previous, rows));
            scheduleDeferredResort();
          } else {
            // Live mode: re-apply the comparator on every update so row order always reflects
            // current status+title severity. `useFlipReorder` animates the resulting movement.
            setResults(applySortedOrder(rows));
          }
        },
        onCompleted: (rows) => {
          if (!isCurrentRun()) return;
          // Cancel any pending deferred re-sort before applying this run's final sort — a no-op
          // when coalescing was never armed.
          clearCoalescingTimers();
          // Anchoring to selection order first (fixed-then-sort only) makes the comparator's
          // equal-key tie-break resolve deterministically to selection order. live mode is already
          // continuously sorted by onRowsChanged, so its finalize step just re-sorts as-is.
          setResults(
            RESULTS_ORDERING_MODE === "fixed-then-sort"
              ? applySortedOrder(anchorToSelectionOrder(rows, selectionOrderRef.current))
              : applySortedOrder(rows)
          );
          // The completion sort has now been applied — any later onRowsChanged snapshot must merge
          // in place rather than re-anchor.
          finalizedRef.current = true;
          setIsRunning(false);
        },
        onError: () => {
          if (!isCurrentRun()) return;
          // Cancel any pending deferred re-sort — a no-op unless live mode with coalescing armed one.
          const hadPendingResort = quietTimerRef.current !== null || maxHoldTimerRef.current !== null;
          clearCoalescingTimers();
          if (hadPendingResort) {
            // A re-sort was deferred when the stream errored — flush it immediately on the latest
            // state, so the list never gets stuck unsorted.
            setResults((current) => applySortedOrder(current));
          }
          setIsRunning(false);
        },
      });
    } catch (error) {
      // Defensive: guard against an unhandled rejection leaving the panel stuck RUNNING.
      if (isCurrentRun()) setIsRunning(false);
      console.error("[RulesPanel] runRulesStreaming failed to start", error);
      return;
    }

    if (isCurrentRun()) {
      controllerRef.current = controller;
    } else {
      // A newer run superseded this one while it was still starting up (data collection can take a
      // while) — tear it down too, so it doesn't keep streaming in the background unattended.
      void controller.cancelRun();
    }
  };

  // Tear down any in-flight run on unmount, so no state update is attempted after unmount and no
  // SSE connection is leaked.
  useEffect(() => {
    return () => {
      runTokenRef.current += 1;
      // Cancel any pending deferred re-sort so its callback can never fire after unmount.
      clearCoalescingTimers();
      void controllerRef.current?.cancelRun();
    };
  }, []);

  const renderEmptyContent = (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <OperationCompletedPanel
        message={formatMessage("cof.validation.title.noruleyet")}
        alt={formatMessage("cof.validation.title.noruleyet")}
        icon={"/assets/illustrations/notFound.svg"}
      />
    </div>
  );

  const renderFooter = ({ selectedIds, selectedCount }) => (
    <FooterPanel>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        {hasRules ? (
          <DefaultButton
            disabled={selectedCount === 0}
            text={
              selectedCount === rules.length
                ? formatMessage("cof.validation.checkbox.runallrules")
                : formatMessage("cof.validation.button.runrules").replace("{count}", String(selectedCount))
            }
            onClick={() => runSelected(selectedIds)}
          />
        ) : (
          <span />
        )}
        <PrimaryButton
          data-testid="cof.validation.button.createnewrule"
          text={formatMessage("cof.validation.button.createnewrule")}
          onClick={() => {
            setRuleToEdit(null);
            setShowAddRulesModal(true);
          }}
        />
      </div>
    </FooterPanel>
  );

  if (resultsOpen)
    return (
      <Modal
        isOpen={true}
        styles={{
          main: {
            minHeight: "100%",
            minWidth: "100%",
          },
        }}
      >
        <RulesResultPanel
          results={results}
          onDelete={(ruleId) => setResults((r) => r.filter((e) => e.ruleId !== ruleId))}
          onShowOnDocument={(coObject) => selectCellOrContentControl(coObject)}
          onBack={() => {
            // This Modal has no onDismiss, so onBack is the only way to close the panel — it must
            // also cancel any in-flight run. If onDismiss/ESC/overlay-click is ever added, wire
            // cancelRun() there too.
            setResultsOpen(false);
            // Invalidate this run's token even when controllerRef.current is still null (during the
            // data-collection phase, before the controller exists) — otherwise closing the panel
            // here would be a silent no-op and the controller would keep streaming once it resolved.
            // Bumping the token reuses the same supersede mechanism runSelected uses for a newer run.
            runTokenRef.current += 1;
            if (isRunning) {
              void controllerRef.current?.cancelRun();
            }
          }}
        />
      </Modal>
    );
  return (
    <>
      {deleteConfirm && ruleToDelete && (
        <CustomModal
          modalTitle={formatMessageWithParams("cof.validation.title.rulenamedelete", {
            ruleName: ruleToDelete.title,
          })}
          messageTitle={formatMessageWithParams("cof.validation.message.confirm.deleterule", {
            ruleName: ruleToDelete.title,
          })}
          message={undefined}
          type="delete"
          onHide={() => {}}
          actions={[
            { title: formatMessage("cof.base.noconfirmdelete"), onClick: cancelDelete, variant: "blue" },
            { title: formatMessage("cof.base.confirmdelete"), onClick: confirmDelete, variant: "red_filled" },
          ]}
        />
      )}
      {showAddRulesModal && (
        <AddRuleModal
          tags={props.tags}
          rules={rules}
          updateRules={props.updateRules}
          isShown={showAddRulesModal}
          onHide={() => setShowAddRulesModal(false)}
          selectedRule={ruleToEdit}
        />
      )}

      <AccordionListPanel
        items={accordionItems}
        filteredItems={filteredAccordionItems}
        expanded={expanded}
        searchQuery={search}
        actionGroups={actionGroups}
        onAccordionToggle={handleAccordionToggle}
        onFilter={handleFilter}
        withSelection={true}
        itemContent={renderItemContent}
        emptyContent={renderEmptyContent}
        noItemsFoundMessage={formatMessage("cof.validation.info.norulematch")}
        footer={renderFooter}
        automationIdSuffix="rule"
      />
    </>
  );
};
